import { SUBSCRIBE_TICKET_COUNT_MAP } from '@entities/subscribes/model'
import { type Locale, type TranslationValues } from '@shared/config/i18n'
import { dayjs } from '@shared/libs/dayjs'
import { formatKrwAmount } from '@shared/utils'
import { match, P } from 'ts-pattern'

import { CouponModel } from '../model'

type Translate = (key: string, values?: TranslationValues) => string

type CouponApplyTarget = {
  paymentType: string
  lessonCountPerMonth: number | null
  curriculumType: string | null
  promotionType: string | null
  langType: string
  lessonTime: number
  lessonMonth: number
}

const NUMBER_LOCALE_BY_LOCALE = {
  ko: 'ko-KR',
  en: 'en-US',
  ja: 'ja-JP',
} satisfies Record<Locale, string>

const COUPON_DATE_FORMAT_BY_LOCALE = {
  ko: 'YYYY.MM.DD',
  en: 'MMM D, YYYY',
  ja: 'YYYY.MM.DD',
} satisfies Record<Locale, string>

const PAYMENT_TYPE_MESSAGE_KEY = {
  LUMP_SUM: 'coupon.paymentType.lumpSum',
  SUBSCRIBE: 'coupon.paymentType.subscribe',
  IPAD: 'coupon.paymentType.ipad',
  EXTEND: 'coupon.paymentType.extend',
  TRIAL: 'coupon.paymentType.trial',
  TRIAL_FREE: 'coupon.paymentType.trialFree',
  BUSINESS: 'coupon.paymentType.business',
  SMART_TALK: 'coupon.paymentType.smartTalk',
  SMART_TALK_TRIAL: 'coupon.paymentType.smartTalkTrial',
} as const

const LANGUAGE_TYPE_MESSAGE_KEY = {
  KR: 'coupon.languageType.kr',
  EN: 'coupon.languageType.en',
  JP: 'coupon.languageType.jp',
  ENJP: 'coupon.languageType.enjp',
} as const

const CURRICULUM_MESSAGE_KEY = {
  BASIC: 'coupon.curriculum.basic',
  BUSINESS: 'coupon.curriculum.business',
  SMART_TALK: 'coupon.curriculum.smartTalk',
} as const

const formatNumber = (value: number, locale: Locale) => {
  return new Intl.NumberFormat(NUMBER_LOCALE_BY_LOCALE[locale]).format(value)
}

export const formatCouponDate = (date: string | null | undefined, timezone: string, locale: Locale) => {
  if (!date) {
    return ''
  }

  return dayjs.utc(date).tz(timezone).locale(locale).format(COUPON_DATE_FORMAT_BY_LOCALE[locale])
}

const formatCouponAmount = (amount: number, locale: Locale, currency: string) =>
  currency === 'KRW'
    ? formatKrwAmount(amount, locale)
    : new Intl.NumberFormat(NUMBER_LOCALE_BY_LOCALE[locale], {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amount)

export const formatCouponDiscountText = ({
  discountType,
  discountAmount,
  discountAmountMax,
  isMaximumDiscount = true,
  isSubscribePayment,
  includeDiscountLabel,
  currency = 'KRW',
  locale,
  t,
}: {
  discountType: keyof typeof CouponModel.DISCOUNT_TYPE
  discountAmount: number
  discountAmountMax?: number
  isMaximumDiscount?: boolean
  isSubscribePayment: boolean
  includeDiscountLabel: boolean
  currency?: string
  locale: Locale
  t: Translate
}) => {
  if (discountType === CouponModel.DISCOUNT_TYPE.FIXED) {
    const amount = formatCouponAmount(discountAmount, locale, currency)
    const key = match({ isSubscribePayment, includeDiscountLabel })
      .with({ isSubscribePayment: true, includeDiscountLabel: true }, () => 'coupon.discount.monthlyFixedLabel')
      .with({ isSubscribePayment: true, includeDiscountLabel: false }, () => 'coupon.discount.monthlyFixedValue')
      .with({ isSubscribePayment: false, includeDiscountLabel: true }, () => 'coupon.discount.fixedLabel')
      .with({ isSubscribePayment: false, includeDiscountLabel: false }, () => 'coupon.discount.fixedValue')
      .exhaustive()

    return t(key, { amount })
  }

  const percent = formatNumber(discountAmount, locale)
  const key = match({ isSubscribePayment, includeDiscountLabel })
    .with({ isSubscribePayment: true, includeDiscountLabel: true }, () => 'coupon.discount.monthlyPercentLabel')
    .with({ isSubscribePayment: true, includeDiscountLabel: false }, () => 'coupon.discount.monthlyPercentValue')
    .with({ isSubscribePayment: false, includeDiscountLabel: true }, () => 'coupon.discount.percentLabel')
    .with({ isSubscribePayment: false, includeDiscountLabel: false }, () => 'coupon.discount.percentValue')
    .exhaustive()
  const baseText = t(key, { percent })

  if (includeDiscountLabel || isMaximumDiscount || discountAmountMax === undefined) {
    return baseText
  }

  return `${baseText} ${t('coupon.discount.maxSuffix', {
    amount: formatCouponAmount(discountAmountMax, locale, currency),
  })}`
}

export const formatCouponMaxDiscountText = ({
  discountAmountMax,
  currency = 'KRW',
  isSubscribePayment,
  locale,
  t,
}: {
  discountAmountMax: number
  currency?: string
  isSubscribePayment: boolean
  locale: Locale
  t: Translate
}) => {
  return t(isSubscribePayment ? 'coupon.discount.monthlyMaxLabel' : 'coupon.discount.maxLabel', {
    amount: formatCouponAmount(discountAmountMax, locale, currency),
  })
}

export const formatCouponPaymentType = (paymentType: string, t: Translate) => {
  const messageKey = PAYMENT_TYPE_MESSAGE_KEY[paymentType as keyof typeof PAYMENT_TYPE_MESSAGE_KEY]

  return messageKey ? t(messageKey) : paymentType
}

export const formatCouponApplyTargetText = (applyTarget: CouponApplyTarget, t: Translate) => {
  const lesson = match({
    paymentType: applyTarget.paymentType,
    lessonCountPerMonth: applyTarget.lessonCountPerMonth,
  })
    .with({ lessonCountPerMonth: -1 }, () => t('subscribeList.nCount.title'))
    .with({ paymentType: 'IPAD' }, () => t('coupon.lessonTarget.ipadPackage'))
    .with(
      {
        paymentType: P.union('LUMP_SUM', 'SUBSCRIBE', 'EXTEND'),
        lessonCountPerMonth: SUBSCRIBE_TICKET_COUNT_MAP.UNLIMITED,
      },
      () => t('coupon.lessonTarget.unlimitedTicket'),
    )
    .with(
      {
        paymentType: P.union('LUMP_SUM', 'SUBSCRIBE', 'EXTEND'),
        lessonCountPerMonth: P.union(
          4,
          SUBSCRIBE_TICKET_COUNT_MAP.PER_MONTH_8,
          SUBSCRIBE_TICKET_COUNT_MAP.PER_MONTH_12,
        ),
      },
      ({ lessonCountPerMonth }) => t('coupon.lessonTarget.perMonthTicket', { count: lessonCountPerMonth }),
    )
    .otherwise(() => '')

  const languageKey = LANGUAGE_TYPE_MESSAGE_KEY[applyTarget.langType as keyof typeof LANGUAGE_TYPE_MESSAGE_KEY]
  const curriculumKey = CURRICULUM_MESSAGE_KEY[applyTarget.curriculumType as keyof typeof CURRICULUM_MESSAGE_KEY]
  const promotion = applyTarget.promotionType === 'PAYBACK' ? t('coupon.promotion.paybackPrefix') : ''

  return t('coupon.lessonTarget.template', {
    promotion,
    language: languageKey ? t(languageKey) : applyTarget.langType,
    curriculum: curriculumKey ? t(curriculumKey) : '',
    lesson,
    minutes: t('coupon.lessonTarget.minutes', { count: applyTarget.lessonTime }),
    months: t('coupon.lessonTarget.months', { count: applyTarget.lessonMonth }),
  })
}
